import OS from './OS';
import MediaLib from './MediaLib';
import {setCanvasSize, drawThumbnail} from '../utils/lib';
import SVG2Canvas from '../utils/SVG2Canvas';
import {encodeUTF8Base64} from '../utils/encodeUTF8Base64';

const database = 'projects';
const collectLibraryAssets = false;

// Sharing state
let zipFileName = '';
let shareName = '';

export default class IO {
    static get zipFileName () {
        return zipFileName;
    }

    static get shareName () {
        return shareName;
    }

    /**
     * Synchronous requests are normally not recommended, but in this case we're
     * going to file URLs so this should be okay.
     */
    static requestSynchronous (url) {
        var request = new XMLHttpRequest();
        request.open('GET', url, false);
        request.send(null);
        if (request.status === 0 || request.status === 200) {
            return request.responseText;
        } else {
            // Failed synchronous loading
            return '';
        }
    }

    static requestFromServer (url, whenDone) {
        var xmlrequest = new XMLHttpRequest();
        xmlrequest.addEventListener('error', transferFailed, false);
        xmlrequest.onreadystatechange = function () {
            if (xmlrequest.readyState == 4) {
                whenDone(xmlrequest.responseText);
            }
        };
        xmlrequest.open('GET', url, true);
        xmlrequest.send(null);
        function transferFailed (e) {
            e.preventDefault();
            e.stopPropagation();
            // Failed loading
        }
    }

    static getThumbnail (str, w, h, destw, desth) {
        str = str.replace(/>\s*</g, '><');
        var xmlDoc = new DOMParser().parseFromString(str, 'text/xml');
        var extxml = document.importNode(xmlDoc.documentElement, true);
        if (extxml.childNodes[0].nodeName == '#comment') {
            extxml.removeChild(extxml.childNodes[0]);
        }
        var srccnv = document.createElement('canvas');
        setCanvasSize(srccnv, w, h);
        var ctx = srccnv.getContext('2d');
        for (var i = 0; i < extxml.childElementCount; i++) {
            SVG2Canvas.drawLayer(extxml.childNodes[i], ctx);
        }
        if (!destw || !desth) {
            return srccnv.toDataURL('image/png');
        }
        var cnv = document.createElement('canvas');
        setCanvasSize(cnv, destw, desth);
        drawThumbnail(srccnv, cnv);
        return cnv.toDataURL('image/png');
    }

    // in iOS casting an svg url in a img.src works except when the SVG has images.
    // This code avoids that bug
    // also when in debug mode you need to get the base64 to avoid sandboxing issues
    static getAsset (md5, fcn) { // returns either a link or a base64 dataurl
        // Fast-path: if md5 already points to a served asset under backgrounds/ or sprites/
        // return the URL directly to avoid unnecessary XHR/base64 work that can hang loading.
        try {
            if (typeof md5 === 'string' && /^(backgrounds|sprites)\//.test(md5)) {
                fcn(md5);
                return;
            }
        } catch (_) {}

        if (MediaLib.keys[md5]) {
            fcn(MediaLib.path + md5); return;
        } // just url link assets do not have photos
        if (md5.indexOf('/') > -1) {
            IO.requestFromServer(md5, gotit); // get url contents
            return;
        }

        OS.getmedia(md5, nextStep);

        function gotit (str) {
            var base64 = IO.getImageDataURL(md5, encodeUTF8Base64(str));
            if (str.indexOf('xlink:href') < 0) {
                fcn(md5); // does not have embedded images
            } else {
                IO.getImagesInSVG(str, function () {
                    fcn(base64);
                });
            } // base64 dataurl
        }

        function nextStep (dataurl) {
            // BUG #4 FIX: Check if dataurl is actually a data URL or just a filename
            // io_setmedianame sometimes returns just the filename (e.g., "proj_123_abc.png")
            // instead of the actual data URL, which causes atob() to crash
            if (!dataurl || typeof dataurl !== 'string') {
                console.warn('⚠️ getAsset: Empty media data; using UI fallback if available');
                fcn(''); // Return empty to let callers decide on a visual fallback
                return;
            }

            // If it looks like a data URL, process it normally
            if (dataurl.startsWith('data:')) {
                try {
                    var str = atob(dataurl.split(',')[1] || dataurl); // Handle full data URL or just base64
                    var base64 = IO.getImageDataURL(md5, dataurl.split(',')[1] || dataurl);
                    IO.getImagesInSVG(str, function () {
                        fcn(base64);
                    });
                } catch (e) {
                    console.error('❌ getAsset: Failed to decode data URL:', e.message);
                    fcn('');
                }
                return;
            }

            // BUG #4 FIX: If it's a filename, try to load it from localStorage
            if (dataurl.indexOf('.') > -1 && dataurl.length < 200) {
                // This looks like a filename - try to load the actual data from localStorage
                var storageKey = 'scratchjr_media_' + dataurl;
                var storedData = null;
                try {
                    storedData = localStorage.getItem(storageKey);
                } catch (e) {
                    console.error('❌ getAsset: Failed to load from localStorage:', e.message);
                }

                if (storedData && storedData.startsWith('data:')) {
                    // Successfully loaded the data URL from storage
                    console.log('✅ getAsset: Loaded thumbnail from localStorage:', dataurl);
                    fcn(storedData);
                    return;
                }

                // Filename found but no data in storage
                console.warn('⚠️ getAsset: Thumbnail filename found but no data in localStorage:', dataurl);
                fcn(''); // Return empty
                return;
            }

            // Fallback: try to decode as base64 (original behavior)
            try {
                var str = atob(dataurl);
                var base64 = IO.getImageDataURL(md5, dataurl);
                IO.getImagesInSVG(str, function () {
                    fcn(base64);
                });
            } catch (e) {
                console.error('❌ getAsset: Failed to decode as base64:', e.message, 'value:', dataurl);
                fcn(''); // Return empty to prevent crash
            }
        }
    }

    static getImagesInSVG (str, whenDone) {
        str = str.replace(/>\s*</g, '><');
        if (str.indexOf('xlink:href') < 0) {
            whenDone(); // needs this in case of reading a PNG in debug mode
        } else {
            loadInnerImages(str, whenDone);
        }

        function loadInnerImages (str, whenDone) {
            var xmlDoc = new DOMParser().parseFromString(str, 'text/xml');
            var extxml = document.importNode(xmlDoc.documentElement, true);
            if (extxml.childNodes[0].nodeName == '#comment') {
                extxml.removeChild(extxml.childNodes[0]);
            }
            var images = IO.getImages(extxml, []);
            var imageCount = images.length;
            for (var i = 0; i < images.length; i++) {
                var dataurl = images[i].getAttribute('xlink:href');
                var svgimg = document.createElement('img');
                svgimg.src = dataurl;
                if (!svgimg.complete) {
                    svgimg.onload = function () {
                        readToLad();
                    };
                } else {
                    readToLad();
                }
            }

            function readToLad () {
                imageCount--;
                if (imageCount < 1) {
                    extxml = null;
                    xmlDoc = null;
                    whenDone();
                }
            }
        }
    }

    static getImages (p, res) {
        for (var i = 0; i < p.childNodes.length; i++) {
            var elem = p.childNodes[i];
            if (elem.nodeName == 'metadata') {
                continue;
            }
            if (elem.nodeName == 'defs') {
                continue;
            }
            if (elem.nodeName == 'sodipodi:namedview') {
                continue;
            }
            if (elem.nodeName == '#comment') {
                continue;
            }
            if (elem.nodeName == 'image') {
                res.push(elem);
            }
            if (elem.nodeName == 'g') {
                IO.getImages(elem, res);
            }
        }
        return res;
    }
    static getImageDataURL (md5, data) {
        var header = '';
        switch (IO.getExtension(md5)) {
        case 'svg': header = 'data:image/svg+xml;base64,';
            break;
        case 'png': header = 'data:image/png;base64,';
            break;
        }
        return header + data;
    }

    static getObject (md5, fcn) {
        // Issue #201 Fix: Add defensive parameter validation to prevent crash
        if (!md5 || typeof md5 !== 'string') {
            console.error('IO.getObject: Invalid parameter:', md5);
            if (fcn && typeof fcn === 'function') {
                fcn(null); // Call callback with null to handle gracefully
            }
            return;
        }

        if (md5.indexOf('/') > -1) {
            // Loading from URL (e.g., external sample JSON under samples/)
            var gotit = function (str) {
                try {
                    // Normalize external sample format to our expected DB row wrapper
                    var norm = IO.normalizeExternalSampleString(str, md5);
                    fcn(norm);
                } catch (e) {
                    // If normalization fails, return original string
                    fcn(str);
                }
            };
            IO.requestFromServer(md5, gotit);
        } else {
            IO.getObjectinDB(database, md5, fcn);
        }
    }

    // Convert a variety of external sample JSON shapes to a DB-row array string
    // Expected return shape: JSON.stringify([ { name, id, version, deleted, isgift, thumbnail, json, gallery, ctime, mtime } ])
    static normalizeExternalSampleString (str, url) {
        let data;
        try {
            data = JSON.parse(str);
        } catch (_) {
            // Not JSON; return original
            return str;
        }
        // Accept either an array of one or a single object
        let obj = Array.isArray(data) ? (data[0] || {}) : data;
        // Lowercase top-level keys for consistency
        obj = IO.parseProjectData(obj);
        // Ensure nested json is an object
        if (typeof obj.json === 'string') {
            try { obj.json = JSON.parse(obj.json); } catch (_) {}
        }
        if (!obj.json || typeof obj.json !== 'object') {
            obj.json = { pages: ['page 1'], currentPage: 'page 1', 'page 1': { sprites: [], md5: 'backgrounds/Farm.svg', num: 1, textstartat: 36, layers: [] } };
        }
        const pageKey = obj.json.currentPage || (obj.json.pages && obj.json.pages[0]) || 'page 1';
        if (!obj.json[pageKey]) {
            obj.json[pageKey] = { sprites: [], md5: 'backgrounds/Farm.svg', num: 1, textstartat: 36, layers: [] };
            if (!obj.json.pages) obj.json.pages = ['page 1'];
            obj.json.currentPage = obj.json.pages[0];
        }
        // Normalize background md5 to our library path when missing a directory
        try {
            const bkg = obj.json[pageKey].md5;
            if (bkg && bkg.indexOf('/') < 0) {
                obj.json[pageKey].md5 = 'backgrounds/' + bkg;
            }
        } catch (_) {}
        // Ensure thumbnail
        if (!obj.thumbnail || !obj.thumbnail.md5) {
            obj.thumbnail = { pagecount: 1, md5: obj.json[pageKey].md5 || 'backgrounds/Farm.svg' };
        } else if (obj.thumbnail.md5.indexOf('/') < 0 && obj.thumbnail.md5.indexOf('.svg') > -1) {
            // Put backgrounds in correct folder if only filename provided
            obj.thumbnail.md5 = 'backgrounds/' + obj.thumbnail.md5;
        }
        // Required metadata defaults
        if (!obj.name) {
            obj.name = (url && typeof url === 'string') ? url.split('/').pop().replace(/\.txt$/,'') : 'Sample';
        }
        if (!obj.id) {
            obj.id = 'sample_' + obj.name.toLowerCase().replace(/\s+/g, '_');
        }
        if (!obj.version) obj.version = 'iOSv01';
        if (!obj.deleted) obj.deleted = 'NO';
        if (!obj.isgift) obj.isgift = '0';
        if (!obj.gallery) obj.gallery = 'samples';
        if (!obj.ctime) obj.ctime = String(Date.now());
        if (!obj.mtime) obj.mtime = String(Date.now());

        // CRITICAL FIX: Stringify json and thumbnail before returning
        // The database module expects these as strings, not objects
        if (obj.json && typeof obj.json === 'object') {
            obj.json = JSON.stringify(obj.json);
        }
        if (obj.thumbnail && typeof obj.thumbnail === 'object') {
            obj.thumbnail = JSON.stringify(obj.thumbnail);
        }

        // Return as a DB-row array string
        return JSON.stringify([obj]);
    }

    static getObjectinDB (db, md5, fcn) {
        var json = {};
        json.stmt = 'select * from ' + db + ' where id = ?';
        json.values = [md5];
        OS.query(json, fcn);
    }

    static setMedia (data, type, fcn) {
        OS.setmedia(btoa(data), type, fcn);
    }

    static query (type, obj, fcn) {
        var json = {};
        json.stmt = 'select ' + obj.items + ' from ' + type +
            ' where ' + obj.cond + (obj.order ? ' order by ' + obj.order : '');
        json.values = obj.values;
        OS.query(json, fcn);
    }

    static deleteobject (type, id, fcn) {
        var json = {};
        json.stmt = 'delete from ' + type + ' where id = ?';
        json.values = [id];
        OS.stmt(json, fcn);
    }

    ////////////////////////
    // projects
    ///////////////////////
    /*
        +[id] =>  // SQL ID creates this
        [deleted] =>
        [name] =>
        [json] => project data
        [thumb] =>
        [mtime] => modification time
    */

    static createProject (obj, fcn) {
        var json = {};
        var keylist = ['name', 'version', 'deleted', 'mtime', 'isgift'];
        var values = '?,?,?,?,?';
        var mtime = (new Date()).getTime().toString();
        var isGift = obj.isgift ? obj.isgift : '0';
        json.values = [obj.name, obj.version, 'NO', mtime, isGift];

        // Support explicit ID for new projects (Issue #559)
        if (obj.id) {
            keylist.unshift('id'); // Add id as first column
            values = '?,' + values; // Add placeholder for id value
            json.values.unshift(obj.id); // Add id value as first value
        }

        // CRITICAL FIX (Issue #559): Generate default project data if missing
        // When creating from lobby, json/thumbnail are not provided
        // Without this, projects are created with empty data and can't be loaded
        if (!obj.json) {
            // Create project structure with default penguin sprite (ScratchJr starter character)
            // Sprite structure matches Animal Friends sample project format
            obj.json = {
                pages: ['page 1'],
                currentPage: 'page 1',
                'page 1': {
                    textstartat: 36,
                    sprites: ['Penguin 1'],  // Default penguin sprite
                    md5: 'backgrounds/Farm.svg',
                    num: 1,
                    lastSprite: 'Penguin 1',
                    // Default penguin sprite definition (matches Animal Friends format)
                    'Penguin 1': {
                        shown: true,
                        type: 'sprite',
                        md5: 'Penguin.svg',
                        id: 'Penguin 1',
                        flip: false,
                        name: 'Penguin',
                        angle: 0,
                        scale: 0.5,
                        speed: 2,
                        defaultScale: 0.5,
                        sounds: ['pop.mp3'],
                        xcoor: 240,
                        ycoor: 180,
                        cx: 27,  // Penguin center x
                        cy: 35,  // Penguin center y
                        w: 54,   // Penguin width
                        h: 70,   // Penguin height
                        homex: 240,
                        homey: 180,
                        homescale: 0.5,
                        homeshown: true,
                        homeflip: false,
                        scripts: []
                    },
                    layers: ['Penguin 1']
                }
            };
        }

        if (!obj.thumbnail) {
            // Create default thumbnail referencing the default background
            obj.thumbnail = {
                pagecount: 1,
                md5: 'backgrounds/Farm.svg'
            };
        }

        // Now add json and thumbnail to INSERT (they're guaranteed to exist)
        addValue('json', JSON.stringify(obj.json));
        addValue('thumbnail', JSON.stringify(obj.thumbnail));

        json.stmt = 'insert into ' + database + ' (' + keylist.toString() + ') values (' + values + ')';

        // CRITICAL FIX (Issue #559): Parse the database response to extract the ID
        // OS.stmt returns JSON like: {"status":"ok","id":"proj_123"}
        // We need to pass just the ID to the callback, not the entire JSON
        OS.stmt(json, function(response) {
            let projectId = response;

            // Parse JSON response if needed
            if (typeof response === 'string' && response.includes('"status"')) {
                try {
                    const parsed = JSON.parse(response);
                    if (parsed.id) {
                        projectId = parsed.id;
                        console.log('📋 IO.createProject: Extracted ID from JSON response:', projectId);
                    }
                } catch (e) {
                    console.warn('⚠️ IO.createProject: Could not parse response, using raw value:', response);
                }
            }

            // Call the original callback with just the ID
            if (fcn) {
                fcn(projectId);
            }
        });
        function addValue (key, str) {
            keylist.push(key);
            values += ',?';
            json.values.push(str);
        }
    }

    static saveProject (obj, fcn) {
        var json = {};
        var keylist = ['version = ?', 'deleted = ?', 'name = ?', 'json = ?', 'thumbnail = ?', 'mtime = ?'];
        json.values = [obj.version, obj.deleted, obj.name, JSON.stringify(obj.json),
            JSON.stringify(obj.thumbnail), (new Date()).getTime().toString()];
        json.stmt = 'update ' + database + ' set ' + keylist.toString() + ' where id = ' + obj.id;
        OS.stmt(json, fcn);
    }

    // Since saveProject is changing the modified time of the project,
    // let's just simply update the isgift flag in a separate function...
    static setProjectIsGift (obj, fcn) {
        var json = {};
        var keylist = ['isgift = ?'];
        json.values = [obj.isgift];
        json.stmt = 'update ' + database + ' set ' + keylist.toString() + ' where id = ' + obj.id;
        OS.stmt(json, fcn);
    }

    static getExtension (str) {
        return str.substring(str.indexOf('.') + 1, str.length);
    }

    static getFilename (str) {
        return str.substring(0, str.indexOf('.'));
    }

    static parseProjectData (data) {
        var res = new Object();
        for (var key in data) {
            res[key.toLowerCase()] = data[key];
        }
        return res;
    }

    //////////////////
    // Sharing
    ////////////////////

    static compressProject (projectReference, finished) {
        IO.getObject(projectReference, function (projectFromDB) {
            var projectMetadata = {
                'thumbnails': [],
                'characters': [],
                'backgrounds': [],
                'sounds': []
            };
            var jsonData = IO.parseProjectData(JSON.parse(projectFromDB)[0]);

            // Collect project assets for inclusion in zip file
            // Parse JSON representations of project data / thumbnail into usable types
            if (typeof jsonData.json == 'string') {
                jsonData.json = JSON.parse(jsonData.json);
            }
            if (typeof jsonData.thumbnail == 'string') {
                jsonData.thumbnail = JSON.parse(jsonData.thumbnail);
            }

            // Method to determine if a particular asset needs to be collected
            // If it does, save the reference in projectMetadata for collection
            var collectAsset = function (assetType, md5) {
                if (md5 && (typeof md5 !== 'undefined')) {
                    if (md5.indexOf('samples/') < 0) { // Exclude sample assets
                        if (collectLibraryAssets) {
                            // Behavior if we want to collect and package library assets
                            if (projectMetadata[assetType].indexOf(md5) < 0 && MediaLib.sounds.indexOf(md5) < 0) {
                                projectMetadata[assetType].push(md5);
                            }
                        } else {
                            // Otherwise, first check if it's in the library
                            if (md5 && (typeof md5 !== 'undefined') &&
                                !MediaLib.keys[md5] && MediaLib.sounds.indexOf(md5) < 0) {
                                if (projectMetadata[assetType].indexOf(md5) < 0) {
                                    projectMetadata[assetType].push(md5);
                                }
                            }
                        }
                    }
                }
            };

            // Project thumbnail
            collectAsset('thumbnails', jsonData.thumbnail.md5);

            var projectData = jsonData.json;

            // Data for each page
            for (var p = 0; p < projectData.pages.length; p++) {
                var pageReference = projectData.pages[p];
                var page = projectData[pageReference];

                // Page background
                collectAsset('backgrounds', page.md5);

                // Sprites
                for (var s = 0; s < page.sprites.length; s++) {
                    var spriteReference = page.sprites[s];
                    var sprite = page[spriteReference];

                    if (sprite.type != 'sprite') {
                        continue;
                    }

                    // Sprite image
                    collectAsset('characters', sprite.md5);

                    // Sprite's recorded sounds
                    for (var snd = 0; snd < sprite.sounds.length; snd++) {
                        collectAsset('sounds', sprite.sounds[snd]);
                    }
                }
            }

            // Now the UI should wait for actual media count to equal expected media count
            // This could pause if getmedia takes a long time, for example,
            // if we have many large sprites or large sounds

            // strip spaces and sanitize filename, including windows reserved names even though
            // kids are unlikely to name their project lpt1 etc.
            var illegalRe = /[\/\?<>\\:\*\|":]/g;
            var controlRe = /[\x00-\x1f\x80-\x9f]/g;
            var reservedRe = /^\.+$/;
            var windowsReservedRe = /^(con|prn|aux|nul|com[0-9]|lpt[0-9])(\..*)?$/i;
            var windowsTrailingRe = /[\. ]+$/;

            zipFileName = jsonData.name.replace(/\s*/g, '');
            zipFileName = zipFileName
                .replace(illegalRe, '_')
                .replace(controlRe, '_')
                .replace(reservedRe, '_')
                .replace(windowsReservedRe, '_')
                .replace(windowsTrailingRe, '_');
            shareName = jsonData.name;

            // create zip natively
            OS.createZipForProject(JSON.stringify(jsonData), projectMetadata, zipFileName, function (name) {
                finished(name);
            });
        });
    }

    static uniqueProjectName (jsonData, callback, useOne) {
        // Ensure the project name is not a duplicate
        console.log('🏷️  IO.uniqueProjectName CALLED with:', {
            inputName: jsonData.name,
            useOne: useOne
        });

        // Split project name from trailing number
        // Returns [project name, number]
        // E.g., "Project 2" -> ["Project", 2]
        // "My project" -> ["My project", null];
        var nameAndNumber = function (name) {
            var splitName = name.split(' ');
            var lastPart = splitName.pop();
            if (!isNaN(lastPart)) {
                return {
                    'name': splitName.join(' '),
                    'number': parseInt(lastPart)
                };
            } else {
                return {
                    'name': name,
                    'number': null
                };
            }
        };

        var giftProjectNameParts = nameAndNumber(jsonData.name);
        console.log('🏷️  Parsed input name:', giftProjectNameParts);

        // Get project names already existing in the DB
        var json = {};
        json.cond = 'deleted = ? AND gallery IS NULL';
        json.items = ['name'];
        json.values = ['NO'];
        IO.query(OS.database, json, function (existingProjects) {
            var newNumber = null;

            existingProjects = JSON.parse(existingProjects);
            console.log('🏷️  Found', existingProjects.length, 'existing projects');

            for (var i = 0; i < existingProjects.length; i++) {
                var existingProjectName = IO.parseProjectData(existingProjects[i]).name;
                var existingProjectNameParts = nameAndNumber(existingProjectName);
                console.log('🏷️  Checking existing project:', {
                    name: existingProjectName,
                    parsed: existingProjectNameParts,
                    matchesBaseName: giftProjectNameParts.name == existingProjectNameParts.name
                });

                if (giftProjectNameParts.name == existingProjectNameParts.name) {
                    if (existingProjectNameParts.number != null) {
                        // CRITICAL FIX (Issue #607): Take the MAXIMUM, not just overwrite
                        // This ensures "Project 1" + "Project 2" → "Project 3", not "Project 2"
                        var candidate = existingProjectNameParts.number + 1;
                        if (newNumber === null || candidate > newNumber) {
                            newNumber = candidate;
                        }
                        console.log('🏷️  Found numbered match, candidate:', candidate, 'current max:', newNumber);
                    } else {
                        // "My project" -> "My project 2"
                        if (newNumber === null || 2 > newNumber) {
                            newNumber = 2;
                        }
                        console.log('🏷️  Found unnumbered match, ensuring at least: 2');
                    }
                }

            }

            console.log('🏷️  Final decision:', {
                newNumber: newNumber,
                useOne: useOne,
                giftHasNumber: giftProjectNameParts.number
            });

            if (newNumber != null && (!giftProjectNameParts.number || newNumber > giftProjectNameParts.number)) {
                // A duplicate project name exists - update it
                jsonData.name = giftProjectNameParts.name + ' ' + newNumber;
                console.log('🏷️  Set name to (duplicate path):', jsonData.name);
            } else if (useOne) {
                jsonData.name = giftProjectNameParts.name + ' 1';
                console.log('🏷️  Set name to (useOne path):', jsonData.name);
            }
            console.log('🏷️  Calling callback with final name:', jsonData.name);
            callback(jsonData);
        });
    }

}
